import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, ArrowPathIcon, ArrowRightIcon } from '@heroicons/react/24/outline';
import { TrashIcon } from '@heroicons/react/24/solid';

import { PetPawIcon } from '@/components/icons/PetPawIcon';
import { useToast } from '@/hooks/useToast';
import { deletePetPlanImportById, listPetPlanImports } from '../services/petPlanImportsService';
import type { PetPlanImportDocument } from '../services/petPlanTypes';

type PetPlanImportRow = {
  id: string;
  competence: string;
  totalAmount: number;
  totalDependents: number;
  collaboratorCount: number;
  createdAtLabel: string;
};

export function PetPlanImportsPage() {
  const navigate = useNavigate();
  const { error: showError, success: showSuccess } = useToast();
  const [imports, setImports] = useState<PetPlanImportDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    const fetchData = async () => {
      setLoading(true);
      try {
        const data = await listPetPlanImports();
        if (!active) return;
        setImports(data);
        setLoadError(null);
      } catch (error) {
        console.error('[PetPlanImportsPage] failed to load imports', error);
        if (!active) return;
        setLoadError('Não foi possível carregar as importações.');
        showError('Não foi possível carregar as importações.');
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };
    void fetchData();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formattedImports = useMemo<PetPlanImportRow[]>(() => {
    return imports
      .map((item) => {
        const collaboratorIds = new Set<string>();
        item.recordsPreview.forEach((record: any) => {
          const collaboratorId = typeof record?.collaboratorId === 'string' ? record.collaboratorId : '';
          if (collaboratorId) collaboratorIds.add(collaboratorId);
        });
        return {
          id: item.id,
          competence: item.billingPeriod ?? 'Sem competência',
          totalAmount: item.totals?.amount ?? 0,
          totalDependents: item.totals?.dependents ?? 0,
          collaboratorCount: collaboratorIds.size,
          createdAtLabel: item.createdAt
            ? item.createdAt.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' })
            : '—',
        };
      })
      .sort((a, b) => {
        if (!a.competence) return 1;
        if (!b.competence) return -1;
        return b.competence.localeCompare(a.competence);
      });
  }, [imports]);

  const handleRemoveImport = async (importId: string, competence: string | null) => {
    const label = competence ? ` (${competence})` : '';
    if (!window.confirm(`Remover a importação${label}? Esta ação não pode ser desfeita.`)) return;
    try {
      await deletePetPlanImportById(importId);
      setImports((prev) => prev.filter((item) => item.id !== importId));
      showSuccess('Importação removida.');
    } catch (error) {
      console.error('[PetPlanImportsPage] failed to delete import', error);
      showError('Não foi possível remover a importação.');
    }
  };

  const formatCurrency = (value: number): string =>
    value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/expenses/benefits')}
            className="inline-flex items-center gap-2 text-sm font-semibold text-primary-700 hover:text-primary-800"
          >
            <ArrowLeftIcon className="h-4 w-4" aria-hidden />
            Voltar
          </button>
          <span className="rounded-lg bg-primary-50 p-3 text-primary-700">
            <PetPawIcon className="h-6 w-6" aria-hidden />
          </span>
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-primary-700">Benefícios</p>
            <h1 className="text-2xl font-semibold text-gray-900">Plano pet</h1>
            <p className="text-sm text-gray-600">Acompanhe importações, totais por competência e vinculações.</p>
          </div>
        </div>

        <Link
          to="/expenses/benefits/pet-plan/import"
          className="inline-flex items-center gap-2 rounded-md bg-primary-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:-translate-y-0.5 hover:bg-primary-700"
        >
          Importar
          <ArrowRightIcon className="h-4 w-4" aria-hidden />
        </Link>
      </div>

      <div className="rounded-lg border border-primary-100 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Importações</h2>
            <p className="text-sm text-gray-600">Visualize e gerencie importações recentes.</p>
          </div>
          {loading ? <ArrowPathIcon className="h-5 w-5 animate-spin text-gray-500" aria-hidden /> : null}
        </div>

        {loadError ? (
          <div className="mt-4 rounded-md border border-red-200 bg-red-50 p-4 text-sm text-red-700">{loadError}</div>
        ) : null}

        {!loading && formattedImports.length === 0 && !loadError ? (
          <div className="mt-4 text-sm text-gray-600">Nenhuma importação registrada.</div>
        ) : null}

        {formattedImports.length > 0 ? (
          <div className="mt-4 overflow-auto rounded-md border border-gray-200">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Competência</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Valor total</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Colaboradores</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Dependentes</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Criado em</th>
                  <th className="px-3 py-2 text-left font-semibold text-gray-700">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {formattedImports.map((row) => (
                  <tr key={row.id}>
                    <td className="px-3 py-2">
                      <Link
                        to={`/expenses/benefits/pet-plan/imports/${encodeURIComponent(row.competence)}`}
                        className="text-primary-700 hover:text-primary-800"
                      >
                        {row.competence}
                      </Link>
                    </td>
                    <td className="px-3 py-2 text-gray-800">{formatCurrency(row.totalAmount)}</td>
                    <td className="px-3 py-2 text-gray-800">{row.collaboratorCount}</td>
                    <td className="px-3 py-2 text-gray-800">{row.totalDependents}</td>
                    <td className="px-3 py-2 text-gray-800">{row.createdAtLabel}</td>
                    <td className="px-3 py-2">
                      <button
                        type="button"
                        onClick={() => handleRemoveImport(row.id, row.competence)}
                        className="inline-flex items-center gap-1 rounded-md border border-red-200 px-2 py-1 text-xs font-semibold text-red-700 transition hover:-translate-y-0.5 hover:border-red-300"
                      >
                        <TrashIcon className="h-4 w-4" aria-hidden />
                        Remover
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}
      </div>
    </div>
  );
}
